import React, { useState, useEffect, useCallback } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { getmobilebyemail, sharedCompany } from '../../../redux/actions/company';
import './css.css';
import ManageUserRoleForm from 'components/navbar/ManageUsers/ManageUserRoleForm';
import { useGlobal } from '../../../contexts';
import { useColorMode } from '@chakra-ui/react';

const validationSchema = Yup.object().shape({
   email: Yup.string().email('Invalid email format').required('Email is required'),
   mobile: Yup.string()
      .matches(/^[0-9]{10}$/, 'Invalid mobile format')
      .required('Mobile is required'),
});

const SharedCompForm = ({ isOpen, onClose, currentRow, selectedSharedComp, isEditMode = false }) => {
   const [errors, setErrors] = useState({});
   const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
   const [disablMobile, setDisablMobile] = useState(false);
   const dispatch = useDispatch();
   const { companyList } = useGlobal();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [row, setRow] = useState({
      email: '',
      mobile: '',
   });

   const [processedData, setProcessedData] = useState(null);
   const [showUserRoleModal, setShowUserRoleModal] = useState(false);
   const [toast, setToast] = useState({ show: false, title: '', message: '', type: '' });

   const clearFormData = useCallback(() => {
      setRow({
         email: '',
         mobile: '',
      });
      setErrors({});
      setProcessedData(null);
   }, []);

   const handleOnClose = useCallback(() => {
      clearFormData();
      onClose();
      setDisablMobile(false);
   }, [clearFormData, onClose]);

   // Handle ESC key press
   useEffect(() => {
      if (!isOpen || showUserRoleModal) return;

      const handleEscKey = (event) => {
         if (event.key === 'Escape' || event.keyCode === 27) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            handleOnClose();
         }
      };

      window.addEventListener('keydown', handleEscKey, true);
      document.addEventListener('keydown', handleEscKey, true);
      document.body.addEventListener('keydown', handleEscKey, true);

      return () => {
         window.removeEventListener('keydown', handleEscKey, true);
         document.removeEventListener('keydown', handleEscKey, true);
         document.body.removeEventListener('keydown', handleEscKey, true);
      };
   }, [isOpen, showUserRoleModal, handleOnClose]);

   const showToast = (title, message, type) => {
      setToast({ show: true, title, message, type });
      setTimeout(() => setToast({ show: false, title: '', message: '', type: '' }), 3000);
   };

   useEffect(() => {
      const timerId = setTimeout(() => {
         setDebouncedSearchQuery(row.email);
      }, 500);

      return () => clearTimeout(timerId);
   }, [row.email]);

   useEffect(() => {
      if (!debouncedSearchQuery) return;

      const fetchData = async () => {
         try {
            setDisablMobile(false);
            const res = await dispatch(getmobilebyemail({ search: debouncedSearchQuery }));
            if (res && res?.mobile) {
               console.log('res', res);
               handleInputChange('mobile', res?.mobile);
               setDisablMobile(true);
            }
         } catch (error) {
            console.error('Error fetching mobile by email:', error);
         }
      };

      fetchData();
   }, [debouncedSearchQuery, dispatch]);

   const handleInputChange = (field, value) => {
      setRow((prev) => ({ ...prev, [field]: value }));

      setErrors((prevErrors) => {
         const newErrors = { ...prevErrors };
         if (newErrors[field]) {
            delete newErrors[field];
         }
         return newErrors;
      });
   };

   const validateStep = async () => {
      try {
         await validationSchema.validate(row, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const errorObj = {};
            err.inner.forEach((error) => {
               errorObj[error.path] = error.message;
            });
            setErrors(errorObj);
            return false;
         }
         return false;
      }
   };

   const prepareUserRights = () => {
      const initialUserRights = {
         dashboard: { add: false, edit: false, deleteRole: false, print: false, view: false },
         // cash: { add: false, edit: false, deleteRole: false, print: false, view: false },
         // bank: { add: false, edit: false, deleteRole: false, print: false, view: false },
         journalvouchers: { add: false, edit: false, deleteRole: false, print: false, view: false },
         reports: { add: false, edit: false, deleteRole: false, print: false, view: false },
         Profile: { add: false, edit: false, deleteRole: false, print: false, view: false },
         companyprofile: { add: false, edit: false, deleteRole: false, print: false, view: false },
         cashReceive: { add: false, edit: false, deleteRole: false, print: false, view: false },
         cashPayment: { add: false, edit: false, deleteRole: false, print: false, view: false },
         bankReceive: { add: false, edit: false, deleteRole: false, print: false, view: false },
         bankPayment: { add: false, edit: false, deleteRole: false, print: false, view: false },

         // Masters
         customer: { add: false, edit: false, deleteRole: false, print: false, view: false },
         vendor: { add: false, edit: false, deleteRole: false, print: false, view: false },
         AllAccount: { add: false, edit: false, deleteRole: false, print: false, view: false },
         item: { add: false, edit: false, deleteRole: false, print: false, view: false },
         category: { add: false, edit: false, deleteRole: false, print: false, view: false },
         brand: { add: false, edit: false, deleteRole: false, print: false, view: false },
         salerepresentative: { add: false, edit: false, deleteRole: false, print: false, view: false },
         itemGroup: { add: false, edit: false, deleteRole: false, print: false, view: false },
         warehouse: { add: false, edit: false, deleteRole: false, print: false, view: false },
         unit: { add: false, edit: false, deleteRole: false, print: false, view: false },
         screen: { add: false, edit: false, deleteRole: false, print: false, view: false },
         daybook: { add: false, edit: false, deleteRole: false, print: false, view: false },
         billtterm: { add: false, edit: false, deleteRole: false, print: false, view: false },
         bankbranch: { add: false, edit: false, deleteRole: false, print: false, view: false },
         transport: { add: false, edit: false, deleteRole: false, print: false, view: false },
         driver: { add: false, edit: false, deleteRole: false, print: false, view: false },
         vehicle: { add: false, edit: false, deleteRole: false, print: false, view: false },
         color: { add: false, edit: false, deleteRole: false, print: false, view: false },
         style: { add: false, edit: false, deleteRole: false, print: false, view: false },
         size: { add: false, edit: false, deleteRole: false, print: false, view: false },
         hsn: { add: false, edit: false, deleteRole: false, print: false, view: false },
         countries: { add: false, edit: false, deleteRole: false, print: false, view: false },
         states: { add: false, edit: false, deleteRole: false, print: false, view: false },
         city: { add: false, edit: false, deleteRole: false, print: false, view: false },
         Area: { add: false, edit: false, deleteRole: false, print: false, view: false },

         // Sale
         saleinvoice: { add: false, edit: false, deleteRole: false, print: false, view: false },
         salereturn: { add: false, edit: false, deleteRole: false, print: false, view: false },
         saleorder: { add: false, edit: false, deleteRole: false, print: false, view: false },
         salechallan: { add: false, edit: false, deleteRole: false, print: false, view: false },

         // Purchase
         purchaseinvoice: { add: false, edit: false, deleteRole: false, print: false, view: false },
         purchasereturn: { add: false, edit: false, deleteRole: false, print: false, view: false },
         purchaseorder: { add: false, edit: false, deleteRole: false, print: false, view: false },
         purchasechallan: { add: false, edit: false, deleteRole: false, print: false, view: false },

         // Inventory
         inventory: { add: false, edit: false, deleteRole: false, print: false, view: false },
         inventoryissue: { add: false, edit: false, deleteRole: false, print: false, view: false },
         inventoryreceive: { add: false, edit: false, deleteRole: false, print: false, view: false },
         inventorystocktransfer: { add: false, edit: false, deleteRole: false, print: false, view: false },
      };

      const data = {
         ...row,
         userRights: [
            {
               ...initialUserRights,
               companyId: selectedSharedComp ? parseInt(selectedSharedComp, 10) : null,
            },
         ],
      };

      return data;
   };

   const onFormSubmit = async () => {
      try {
         const isValid = await validateStep();
         if (!isValid) {
            showToast('Validation Error', 'Please correct the form errors.', 'error');
            return;
         }

         const data = prepareUserRights();
         setProcessedData(data);
         setShowUserRoleModal(true);
      } catch (error) {
         showToast('Error', error.message || 'An error occurred while preparing the data.', 'error');
      }
   };

   const handleManageUserRoleClose = () => {
      setShowUserRoleModal(false);
      clearFormData();
      onClose();
   };

   const handleSaveUserRights = async (updatedData) => {
      try {
         const finalData = {
            ...processedData,
            userRights: updatedData.userRights || processedData.userRights,
         };

         await dispatch(sharedCompany(finalData));
         showToast('Success', 'Company shared successfully', 'success');

         handleManageUserRoleClose();
      } catch (error) {
         showToast('Error', error.message || 'An error occurred while saving the data.', 'error');
      }
   };

   useEffect(() => {
      if (isOpen && isEditMode && currentRow) {
         setRow((prev) => ({
            ...prev,
            email: currentRow?.email,
            mobile: currentRow?.mobile,
         }));
      }
   }, [currentRow, isEditMode, isOpen]);

   if (!isOpen) return null;

   return (
      <>
         {toast.show && (
            <div className="fixed top-4 right-4 z-[99999] animate-fade-in">
               <div
                  className={`rounded-xl shadow-2xl p-4 min-w-[320px] backdrop-blur-sm transition-all ${
                     toast.type === 'success'
                        ? isDark
                           ? 'bg-emerald-900/90 border border-emerald-700/50'
                           : 'bg-emerald-50 border border-emerald-200'
                        : toast.type === 'error'
                          ? isDark
                             ? 'bg-red-900/90 border border-red-700/50'
                             : 'bg-red-50 border border-red-200'
                          : isDark
                            ? 'bg-blue-900/90 border border-blue-700/50'
                            : 'bg-blue-50 border border-blue-200'
                  }`}>
                  <div className="flex items-start gap-3">
                     <div className="flex-shrink-0">
                        {toast.type === 'success' && (
                           <svg
                              className={`h-6 w-6 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`}
                              viewBox="0 0 20 20"
                              fill="currentColor">
                              <path
                                 fillRule="evenodd"
                                 d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                 clipRule="evenodd"
                              />
                           </svg>
                        )}
                        {toast.type === 'error' && (
                           <svg
                              className={`h-6 w-6 ${isDark ? 'text-red-400' : 'text-red-500'}`}
                              viewBox="0 0 20 20"
                              fill="currentColor">
                              <path
                                 fillRule="evenodd"
                                 d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                 clipRule="evenodd"
                              />
                           </svg>
                        )}
                     </div>
                     <div className="flex-1">
                        <h3
                           className={`text-sm font-semibold ${
                              toast.type === 'success'
                                 ? isDark
                                    ? 'text-emerald-200'
                                    : 'text-emerald-800'
                                 : toast.type === 'error'
                                   ? isDark
                                      ? 'text-red-200'
                                      : 'text-red-800'
                                   : isDark
                                     ? 'text-blue-200'
                                     : 'text-blue-800'
                           }`}>
                           {toast.title}
                        </h3>
                        <p
                           className={`mt-1 text-sm ${
                              toast.type === 'success'
                                 ? isDark
                                    ? 'text-emerald-300'
                                    : 'text-emerald-700'
                                 : toast.type === 'error'
                                   ? isDark
                                      ? 'text-red-300'
                                      : 'text-red-700'
                                   : isDark
                                     ? 'text-blue-300'
                                     : 'text-blue-700'
                           }`}>
                           {toast.message}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {!showUserRoleModal && (
            <div className="fixed inset-0 z-[99998] flex items-center justify-center">
               <div
                  className={`absolute inset-0 transition-all ${
                     isDark ? 'bg-gradient-to-br from-slate-900/95 via-slate-800/95 to-slate-900/95' : 'bg-black/50'
                  }`}
                  onClick={handleOnClose}
               />

               <div
                  className={`relative rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-5 z-[9999] transition-all ${
                     isDark
                        ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700/50'
                        : 'bg-white border-2 border-gray-100'
                  }`}>
                  <div
                     className={`flex items-center justify-between mb-4 pb-4 border-b ${
                        isDark ? 'border-slate-700/50' : 'border-gray-100'
                     }`}>
                     <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        Share Company
                     </h2>
                     <button
                        onClick={handleOnClose}
                        className={`rounded-full p-1 transition-colors ${
                           isDark
                              ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        }`}>
                        <svg
                           className="w-6 h-6"
                           fill="none"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           viewBox="0 0 24 24"
                           stroke="currentColor">
                           <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                     </button>
                  </div>

                  <div className="mb-6">
                     <div className="grid gap-1">
                        <div className="col-span-4">
                           <div className="grid gap-2">
                              <div className="seperator-width">
                                 <div className="flex flex-col">
                                    <label
                                       htmlFor="email"
                                       className={`text-sm font-medium mb-2 ${
                                          isDark ? 'text-slate-200' : 'text-gray-700'
                                       }`}>
                                       Email <span className={isDark ? 'text-red-400' : 'text-red-500'}>*</span>
                                    </label>
                                    <input
                                       id="email"
                                       type="email"
                                       value={row.email}
                                       onChange={(e) => handleInputChange('email', e.target.value.toLowerCase())}
                                       placeholder="Enter email address"
                                       className={`w-full px-4 py-2 rounded-md transition-all focus:outline-none focus:ring-2 ${
                                          errors.email
                                             ? isDark
                                                ? 'bg-slate-800 border-2 border-red-500/50 text-white placeholder-red-400/50 focus:border-red-500 focus:ring-red-500/20'
                                                : 'bg-white border-2 border-red-300 text-gray-900 placeholder-red-400 focus:border-red-500 focus:ring-red-500/20'
                                             : isDark
                                               ? 'bg-slate-800 border-2 border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20'
                                               : 'bg-white border-2 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-green-500/20'
                                       }`}
                                       required
                                    />
                                    {errors.email && (
                                       <span className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                                          {errors.email}
                                       </span>
                                    )}
                                 </div>
                                 <div className="flex flex-col mt-4">
                                    <label
                                       htmlFor="mobile"
                                       className={`text-sm font-medium mb-2 ${
                                          isDark ? 'text-slate-200' : 'text-gray-700'
                                       }`}>
                                       Mobile <span className={isDark ? 'text-red-400' : 'text-red-500'}>*</span>
                                    </label>
                                    <input
                                       id="mobile"
                                       type="number"
                                       value={row.mobile}
                                       onChange={(e) => handleInputChange('mobile', e.target.value)}
                                       placeholder="Enter mobile address"
                                       disabled={disablMobile === true}
                                       className={`w-full px-4 py-2 rounded-md transition-all focus:outline-none focus:ring-2 ${disablMobile ? 'opacity-60 cursor-not-allowed' : ''} ${
                                          errors.mobile
                                             ? isDark
                                                ? 'bg-slate-800 border-2 border-red-500/50 text-white placeholder-red-400/50 focus:border-red-500 focus:ring-red-500/20'
                                                : 'bg-white border-2 border-red-300 text-gray-900 placeholder-red-400 focus:border-red-500 focus:ring-red-500/20'
                                             : isDark
                                               ? 'bg-slate-800 border-2 border-slate-600 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20'
                                               : 'bg-white border-2 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-green-500/20'
                                       }`}
                                       required
                                    />
                                    {errors.mobile && (
                                       <span className={`text-sm mt-1 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                                          {errors.mobile}
                                       </span>
                                    )}
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="flex justify-end gap-3">
                     <button
                        onClick={onFormSubmit}
                        className={`px-6 py-2 rounded-md transition-colors font-medium shadow-sm ${
                           isDark
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
                              : 'bg-green-500 text-white hover:bg-green-600'
                        }`}>
                        Next
                     </button>
                     <button
                        onClick={handleOnClose}
                        className={`px-6 py-2 rounded-md transition-colors font-medium ${
                           isDark
                              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600 border border-slate-600'
                              : 'bg-transparent text-gray-700 hover:bg-gray-100'
                        }`}>
                        Cancel
                     </button>
                  </div>
               </div>
            </div>
         )}

         {showUserRoleModal && processedData && (
            <ManageUserRoleForm
               isOpen={showUserRoleModal}
               onClose={handleManageUserRoleClose}
               selectedMember={{ email: row.email, mobile: row.mobile }}
               companyList={companyList}
               selectedSharedCompId={selectedSharedComp}
               processedData={processedData}
               onSaveUserRights={handleSaveUserRights}
            />
         )}
      </>
   );
};

export default SharedCompForm;

// import React, { useState, useEffect } from 'react';
// import * as Yup from 'yup';
// import {
//   Grid,
//   GridItem,
//   useColorModeValue,
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalCloseButton,
//   ModalBody,
//   ModalFooter,
//   Button,
//   useToast,
// } from '@chakra-ui/react';
// import {
//   renderFormControl,
// } from 'utils/DynamicFormData';
// import { useDispatch } from 'react-redux';
// import {
//   sharedCompany
// } from '../../../redux/actions/company';
// import './css.css';
// import ManageUserRoleForm from 'components/navbar/ManageUsers/ManageUserRoleForm';

// const validationSchema = Yup.object().shape({
//   email: Yup.string().email('Invalid email format').transform(value => value === "" ? null : value).nullable(),
// });

// const SharedCompForm = ({
//   isOpen,
//   onClose,
//   currentRow,
//   selectedSharedComp,
//   isEditMode = false,
// }) => {
//   //   const [activeStep, setActiveStep] = useState(0);
//   const [errors, setErrors] = useState({});
//   const textColor = useColorModeValue('gray.800', 'white');
//   const toast = useToast();
//   const dispatch = useDispatch();
//   var processedData;
//   const [row, setRow] = useState({
//     email: '',
//   });

//   const clearFormData = () => {
//     setRow({
//       email: '',
//     });
//     setErrors({});
//   };

//   const handleOnClose = () => {
//     clearFormData();
//     window.location.reload();
//     onClose();
//   };

//   const handleInputChange = (field, value) => {
//     setRow((prev) => ({ ...prev, [field]: value }));

//     setErrors((prevErrors) => {
//       const newErrors = { ...prevErrors };
//       if (newErrors[field]) {
//         delete newErrors[field];
//       }
//       return newErrors;
//     });
//   };

//   const validateStep = async () => {
//     try {
//       await validationSchema.validate(row, { abortEarly: false });
//       setErrors({});
//       return true;
//     } catch (err) {
//       if (err instanceof Yup.ValidationError) {
//         const errorObj = {};
//         err.inner.forEach((error) => {
//           errorObj[error.path] = error.message;
//         });
//         setErrors(errorObj);
//         return false;
//       }
//       return false;
//     }
//   };

//   const onFormSubmit = async () => {
//     try {
//       processedData = {
//         ...row,
//   userRights: [
//     {
//         "dashboard": {
//             "roleId": 40,
//             "roleName": "Dashboard",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "masters": {
//             "roleId": 1,
//             "roleName": "Masters",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "customer": {
//             "roleId": 2,
//             "roleName": "Customer",
//             "add": true,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 1
//         },
//         "vendor": {
//             "roleId": 3,
//             "roleName": "Vendor",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 1
//         },
//         "item": {
//             "roleId": 4,
//             "roleName": "Item",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 1
//         },
//         "category": {
//             "roleId": 5,
//             "roleName": "Category",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 4
//         },
//         "brand": {
//             "roleId": 6,
//             "roleName": "Brand",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 4
//         },
//         "series": {
//             "roleId": 7,
//             "roleName": "Series",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 4
//         },
//         "item group": {
//             "roleId": 8,
//             "roleName": "Item Group",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 4
//         },
//         "warehouse": {
//             "roleId": 9,
//             "roleName": "Warehouse",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 4
//         },
//         "unit": {
//             "roleId": 10,
//             "roleName": "Unit",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 4
//         },
//         "screen": {
//             "roleId": 11,
//             "roleName": "Screen",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 4
//         },
//         "ledger": {
//             "roleId": 12,
//             "roleName": "Ledger",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 1
//         },
//         "daybook": {
//             "roleId": 13,
//             "roleName": "Daybook",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 1
//         },
//         "bill term": {
//             "roleId": 14,
//             "roleName": "Bill Term",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 13
//         },
//         "bank branch": {
//             "roleId": 15,
//             "roleName": "Bank Branch",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 1
//         },
//         "transport": {
//             "roleId": 16,
//             "roleName": "Transport",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 1
//         },
//         "driver": {
//             "roleId": 21,
//             "roleName": "Driver",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 19
//         },
//         "vehicle": {
//             "roleId": 18,
//             "roleName": "Vehicle",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 16
//         },
//         "other": {
//             "roleId": 19,
//             "roleName": "Other",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 1
//         },
//         "color": {
//             "roleId": 20,
//             "roleName": "Color",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 19
//         },
//         "style": {
//             "roleId": 22,
//             "roleName": "Style",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 19
//         },
//         "size": {
//             "roleId": 23,
//             "roleName": "Size",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 19
//         },
//         "hsn": {
//             "roleId": 24,
//             "roleName": "HSN",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 19
//         },
//         "countries": {
//             "roleId": 25,
//             "roleName": "Countries",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 19
//         },
//         "states": {
//             "roleId": 26,
//             "roleName": "States",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 19
//         },
//         "city": {
//             "roleId": 27,
//             "roleName": "City",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 19
//         },
//         "sale": {
//             "roleId": 28,
//             "roleName": "Sale",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "sale invoice": {
//             "roleId": 29,
//             "roleName": "Sale Invoice",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 28
//         },
//         "sale return": {
//             "roleId": 30,
//             "roleName": "Sale Return",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 28
//         },
//         "purchase": {
//             "roleId": 31,
//             "roleName": "Purchase",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "purchase invoice": {
//             "roleId": 32,
//             "roleName": "Purchase Invoice",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 31
//         },
//         "purchase return": {
//             "roleId": 33,
//             "roleName": "Purchase Return",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": 31
//         },
//         "cash": {
//             "roleId": 34,
//             "roleName": "Cash",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "bank": {
//             "roleId": 35,
//             "roleName": "Bank",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "journal vouchers": {
//             "roleId": 36,
//             "roleName": "Journal Vouchers",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "reports": {
//             "roleId": 37,
//             "roleName": "Reports",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "profile": {
//             "roleId": 38,
//             "roleName": "Profile",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "company profile": {
//             "roleId": 39,
//             "roleName": "Company Profile",
//             "add": false,
//             "edit": false,
//             "deleteRole": false,
//             "print": true,
//             "view": true,
//             "parentroleId": null
//         },
//         "companyId": selectedSharedComp
//     }
// ]
//       };

//       await dispatch(sharedCompany(processedData));

//       clearFormData();
//       onClose();
//       // window.location.reload();
//     } catch (error) {
//       toast({
//         title: 'Error',
//         description:
//           error.message || 'An error occurred while saving the Daybook.',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//       });
//     }
//   };

//   const handleNext = () => {
//     try {
//       // Manually prepare the data to handle nulls
//       const dataToValidate = {};

//       // Validate synchronously
//       const isValid = validationSchema.validateSync(
//         dataToValidate,
//         { abortEarly: false }
//       );

//       if (isValid) {
//         onFormSubmit();
//       }
//     } catch (err) {
//       if (err instanceof Yup.ValidationError) {
//         const errorObj = {};
//         err.inner.forEach((error) => {
//           errorObj[error.path] = error.message;
//         });
//         setErrors(errorObj);

//         toast({
//           title: 'Validation Error',
//           description: 'Please correct the errors.',
//           status: 'error',
//           duration: 3000,
//           isClosable: true,
//         });
//       }
//     }
//   };

//   useEffect(() => {
//     if (isOpen && isEditMode && currentRow) {
//       setRow((prev) => ({
//         ...prev,
//         email: currentRow?.email,
//       }));
//     }
//   }, [currentRow, isEditMode, isOpen]);

//   return (
//     <>
//       <Modal isOpen={isOpen} onClose={handleOnClose} size="xl">
//         <ModalOverlay />
//         <ModalContent
//           borderRadius="20px"
//           p={5}
//           boxShadow="lg"
//         >
//           <ModalHeader fontSize="20px" fontWeight="600" mb={4}>
//             Share Company
//           </ModalHeader>
//           <ModalCloseButton />
//           <ModalBody>
//             <Grid
//               h="auto"
//               gap={1}
//             >
//               <GridItem colSpan={4}>
//                 <Grid  gap={2}>
//                   <>
//                     <GridItem className="seperator-width">
//                       {renderFormControl(
//                         'email',
//                         'Email',
//                         {
//                           value: row.email,
//                           onChange: (e) =>
//                             handleInputChange('email', e.target.value),
//                         },
//                         errors,
//                         textColor,
//                         true, // isRequired
//                       )}
//                     </GridItem>
//                   </>
//                 </Grid>
//               </GridItem>
//             </Grid>
//           </ModalBody>
//           <ModalFooter>
//             <Button onClick={() => handleNext()} colorScheme="green" ml={3}>
//               Save
//             </Button>
//             <Button variant="ghost" onClick={() => handleOnClose()} ml={3}>
//               Cancel
//             </Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//       <ManageUserRoleForm processedData={processedData} />
//     </>
//   );
// };

// export default SharedCompForm;
